
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { supabase, getEdgeFunctionUrl } from "@/integrations/supabase/client";
import { Navbar } from "@/components/Navbar";

export default function CheckoutRedirect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initiateCheckout = async () => {
      try {
        setLoading(true);
        
        // Check if this is a success callback from Stripe
        const session_id = searchParams.get('session_id');
        const success = searchParams.get('success');
        
        if (session_id && success === 'true') {
          // Mark checkout as completed
          sessionStorage.setItem('checkoutComplete', 'true');
          toast({
            title: "Payment Successful",
            description: "Your payment was processed successfully. Now let's complete your profile.",
          });
          
          // Continue to onboarding if there's onboarding data
          const onboardingData = localStorage.getItem('onboardingData');
          if (onboardingData) {
            navigate('/onboarding');
          } else {
            // If no onboarding data, go to dashboard
            navigate('/dashboard');
          }
          return;
        }
        
        // Get the onboarding data if it exists
        const onboardingData = localStorage.getItem('onboardingData');
        // Get plan info from localStorage
        const selectedPlan = localStorage.getItem('selectedPlan') || 'premium';
        const selectedPriceId = localStorage.getItem('selectedPriceId') || 'price_1R1ajwFapiP8fRW6K74yaoPq';
        
        // For premium plans, create checkout
        if (selectedPlan === 'premium') {
          console.log("Creating checkout session for Premium plan");
          
          // Use window.location.origin which will get the correct domain in any environment
          const host = window.location.origin;
          const successUrl = `${host}/checkout/success?success=true`;
          
          // Store minimal onboarding data for checkout
          let checkoutData: { name?: string; email?: string } = {};
          if (onboardingData) {
            try {
              const parsedData = JSON.parse(onboardingData);
              if (parsedData && typeof parsedData === 'object') {
                checkoutData = {
                  name: parsedData.name || '',
                  email: parsedData.email || '',
                };
              }
            } catch (parseError) {
              console.error("Error parsing onboarding data:", parseError);
            }
          }
          
          const onboardingDataString = JSON.stringify(checkoutData);
          
          // Get the Supabase anon key for authorization
          const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 
                          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhvemhpenhwcnZvYXdrcHloZ2NuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE3MjYzMzYsImV4cCI6MjA1NzMwMjMzNn0.BmYGIt-slzGSX0F4ILmH1gRrdim59FgdUm-xq_OLiqA";
          
          // FIRST TRY: Use Supabase SDK for function invocation
          try {
            console.log("Attempting to create checkout using Supabase SDK...");
            const { data, error } = await supabase.functions.invoke('create-checkout', {
              body: {
                email: checkoutData.email || 'user@example.com',
                priceId: selectedPriceId,
                plan: selectedPlan,
                onboardingData: onboardingDataString,
                successUrl
              }
            });

            if (error) {
              console.error("Error invoking function via SDK:", error);
              throw new Error(`Error invoking checkout function: ${error.message}`);
            }

            console.log("Checkout created successfully via SDK:", data);
            
            if (!data || !data.url) {
              throw new Error("Invalid response from checkout function");
            }

            // Store session ID and redirect
            if (data.session_id) {
              sessionStorage.setItem('checkoutSessionId', data.session_id);
            }
            
            window.location.href = data.url;
            return;
            
          } catch (sdkError) {
            console.error("SDK invocation failed, falling back to direct fetch:", sdkError);
            // Continue to fallback method
          }

          // FALLBACK: Use direct fetch with explicit authorization header
          console.log("Falling back to direct fetch with auth headers...");
          const checkoutUrl = getEdgeFunctionUrl('create-checkout');
          console.log("Using checkout URL:", checkoutUrl);

          const response = await fetch(checkoutUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${anonKey}`,
              'apikey': anonKey
            },
            body: JSON.stringify({
              email: checkoutData.email ? checkoutData.email : 'user@example.com',
              priceId: selectedPriceId,
              plan: selectedPlan,
              onboardingData: onboardingDataString,
              successUrl
            })
          });

          console.log("Fetch response status:", response.status);
          
          if (!response.ok) {
            console.error("Checkout creation failed:", response.status, response.statusText);
            const errorText = await response.text();
            console.error("Error response:", errorText);
            throw new Error(`Failed to create checkout session: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();
          console.log("Response data:", data);
          
          if (!data || !data.url) {
            console.error("Invalid response from checkout function:", data);
            throw new Error("Invalid response from checkout function");
          }

          console.log("Checkout session created successfully");
          console.log("Redirecting to checkout URL:", data.url);
          
          // Store the session ID in sessionStorage for verification on redirect
          if (data.session_id) {
            sessionStorage.setItem('checkoutSessionId', data.session_id);
            console.log("Stored checkout session ID:", data.session_id);
          }

          // Redirect to Stripe Checkout
          window.location.href = data.url;
        } else {
          // For free plan, go straight to onboarding
          navigate('/onboarding');
        }
      } catch (error: any) {
        console.error("Error in checkout redirect:", error);
        setError(error.message || "Failed to create checkout session");
        toast({
          title: "Checkout Error",
          description: "There was a problem creating your checkout session. Continuing with free plan.",
          variant: "destructive",
        });
        
        // Fall back to free plan
        localStorage.setItem('selectedPlan', 'free');
        
        // Redirect to onboarding
        setTimeout(() => {
          navigate('/onboarding');
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    initiateCheckout();
  }, [navigate, searchParams, toast]);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 py-12 px-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-card border rounded-lg p-8 shadow-sm text-center">
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold mb-2">Preparing Your Checkout</h2>
              <p className="text-muted-foreground">Just a moment while we set up your payment...</p>
            </>
          ) : error ? (
            <>
              <div className="text-destructive mb-4 text-4xl">⚠️</div>
              <h2 className="text-2xl font-bold mb-2">Checkout Error</h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <p className="text-sm">Redirecting to onboarding with free plan...</p>
            </>
          ) : (
            <>
              <div className="text-primary mb-4 text-4xl">✓</div>
              <h2 className="text-2xl font-bold mb-2">Redirecting to Checkout</h2>
              <p className="text-muted-foreground">You'll be redirected to our secure payment processor...</p>
            </>
          )}
        </div>
      </div>
    </>
  );
}
